// Listing modes for Flight Schedule — Board / Agenda / List.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/ads-and-sbs/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/board    → FlightScheduleBoard  (day-board, the working view)
//   <base>/agenda   → FlightScheduleAgenda (the day's bookings)
//   <base>/list     → FlightScheduleGrid   (flat AeronetDataGrid over flights)

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  board: "Board",
  agenda: "Agenda",
  list: "List",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  board: "Day's flights by aircraft",
  agenda: "The day's bookings",
  list: "Every scheduled flight",
};

/**
 * Board, not List or Agenda. The day-board is the working surface an
 * operator opens this module for; List is the flat-table alternative and
 * Agenda is the day's bookings. Matches the retired chip+menu's own default.
 */
export const DEFAULT_MODE_SEGMENT = "board";

/**
 * `<module>.viewMode` — the exact key the retired chip+menu already used
 * (FlightScheduleList.tsx), so a stored mode from before this migration is
 * still honoured; see readRememberedModeSegment below.
 */
export const MODE_STORAGE_KEY = "flight_schedule.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: the retired switcher's three mode keys ("board",
 * "agenda", "list") are IDENTICAL to the route segments below, so anything
 * already stored under MODE_STORAGE_KEY is already in the right shape.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
